import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState, SkeletonRows, StatCard } from "@/components/common";
import { fmtCompact, fmtNum, fmtUsd } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState(null);
  usePageTitle("Network Analytics", "Robinhood Chain network health: daily transactions, gas prices, total blocks and addresses.");

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/charts/transactions").then((r) => setChart((r.data.chart_data || []).reverse())).catch(() => setChart([]));
  }, []);

  return (
    <div className="space-y-6 pt-8">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Activity size={20} className="text-primary" /> Network Analytics
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Live health and activity metrics for Robinhood Chain.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Blocks" value={stats ? fmtCompact(stats.total_blocks) : "…"} sub={`~${stats?.average_block_time ? (stats.average_block_time / 1000).toFixed(2) : "…"}s block time`} />
        <StatCard label="Total Transactions" value={stats ? fmtCompact(stats.total_transactions) : "…"} sub={stats ? `${fmtCompact(stats.transactions_today)} today` : ""} />
        <StatCard label="Total Addresses" value={stats ? fmtCompact(stats.total_addresses) : "…"} sub="unique accounts" />
        <StatCard label="ETH Price" value={stats?.coin_price ? fmtUsd(stats.coin_price) : "…"} sub="Source: CoinGecko · live" />
      </div>

      <Card className="p-4" data-testid="daily-tx-chart">
        <h2 className="text-sm font-semibold">Daily Transactions</h2>
        {chart === null ? <SkeletonRows n={4} /> : chart.length === 0 ? <EmptyState title="No chart data available yet" /> : (
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart}>
                <defs>
                  <linearGradient id="txGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="currentColor" opacity={0.4} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} stroke="currentColor" opacity={0.4} tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={55} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }}
                  formatter={(v) => [fmtNum(v, { maximumFractionDigits: 0 }), "Transactions"]} />
                <Area type="monotone" dataKey="transactions_count" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#txGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard label="Gas — Slow" value={stats?.gas_prices ? `${stats.gas_prices.slow} gwei` : "…"} />
        <StatCard label="Gas — Average" value={stats?.gas_prices ? `${stats.gas_prices.average} gwei` : "…"} />
        <StatCard label="Gas — Fast" value={stats?.gas_prices ? `${stats.gas_prices.fast} gwei` : "…"} />
      </div>
    </div>
  );
}
